import { useState, useEffect, useMemo } from "react";
import {
  getCurrentBreakpoint,
  ViewportRange,
  ResponsiveValue,
  getResponsiveValue,
  BREAKPOINTS,
  MEDIA_QUERIES,
} from "./breakpoints";

/**
 * Hook to get the current viewport width
 */
export const useViewportWidth = () => {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};

/**
 * Hook to get the current breakpoint
 */
export const useBreakpoint = (): ViewportRange => {
  const width = useViewportWidth();
  return useMemo(() => getCurrentBreakpoint(width), [width]);
};

/**
 * Hook to check if viewport matches a specific breakpoint
 */
export const useMediaQuery = (
  query: keyof typeof MEDIA_QUERIES | string
): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery =
      typeof query === "string"
        ? query
        : MEDIA_QUERIES[query as keyof typeof MEDIA_QUERIES];

    const mq = window.matchMedia(mediaQuery);

    const handleChange = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };

    // Set initial value
    setMatches(mq.matches);

    // Modern browsers
    if (mq.addEventListener) {
      mq.addEventListener("change", handleChange);
      return () => mq.removeEventListener("change", handleChange);
    }
    // Legacy browsers
    else {
      mq.addListener(handleChange);
      return () => mq.removeListener(handleChange);
    }
  }, [query]);

  return matches;
};

/**
 * Hook to get responsive value based on current breakpoint
 */
export const useResponsiveValue = <T>(values: ResponsiveValue<T>): T => {
  const breakpoint = useBreakpoint();
  return useMemo(
    () => getResponsiveValue(values, breakpoint),
    [values, breakpoint]
  );
};

/**
 * Hook to check if viewport is at or below a certain breakpoint
 */
export const useIsBelow = (
  breakpointName: keyof typeof BREAKPOINTS
): boolean => {
  const width = useViewportWidth();
  return width < BREAKPOINTS[breakpointName];
};

/**
 * Hook to check if viewport is at or above a certain breakpoint
 */
export const useIsAbove = (
  breakpointName: keyof typeof BREAKPOINTS
): boolean => {
  const width = useViewportWidth();
  return width >= BREAKPOINTS[breakpointName];
};

/**
 * Hook to check if viewport is between two breakpoints
 */
export const useIsBetween = (
  minBreakpoint: keyof typeof BREAKPOINTS,
  maxBreakpoint: keyof typeof BREAKPOINTS
): boolean => {
  const width = useViewportWidth();
  return (
    width >= BREAKPOINTS[minBreakpoint] && width < BREAKPOINTS[maxBreakpoint]
  );
};

/**
 * Hook for responsive styles
 */
export const useResponsiveStyles = <T extends Record<string, any>>(
  styles: ResponsiveValue<T>
): T => {
  const breakpoint = useBreakpoint();
  return useMemo(
    () => getResponsiveValue(styles, breakpoint),
    [styles, breakpoint]
  );
};
